import * as _ from 'lodash-es';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { Location, To } from 'react-router';
import { useLocation, useNavigationType } from 'react-router';
import useNavigate from './useNavigate';

interface MainContentHistory {
  back: (
    predicate?: (location: Location) => boolean,
    options?: {
      replace?: boolean;
      fallback?: string;
    },
  ) => void;
}

const MainContentHistoryContext = createContext<MainContentHistory>({
  back: () => {},
});

export const MainContentHistoryProvider = ({
  children,
  skipLocation,
}: {
  children: JSX.Element | JSX.Element[];
  skipLocation: boolean;
}) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocationsRef = useRef<Location[]>([]);
  const currentLocationRef = useRef<Location | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    currentLocationRef.current = location;
    const previousLocations = previousLocationsRef.current;
    if (location !== _.last(previousLocations) && !skipLocation) {
      previousLocationsRef.current =
        navigationType === 'REPLACE'
          ? [...previousLocations.slice(0, -1), location]
          : [...previousLocations, location];
    }
  }, [location, skipLocation, navigationType]);

  const back = useCallback(
    (
      predicate: (location: Location) => boolean = () => true,
      options: { fallback?: string; replace?: boolean } = {},
    ) => {
      const previousLocations = previousLocationsRef.current;
      let destination: To | null = null;

      do {
        const prevLocation = previousLocations.pop();
        if (
          prevLocation !== currentLocationRef.current &&
          prevLocation &&
          predicate(prevLocation)
        ) {
          destination = prevLocation;
        }
      } while (!destination && previousLocationsRef.current.length > 0);
      navigate(destination || options.fallback || '/', {
        replace: options.replace,
      });
    },
    [navigate],
  );

  const value = useMemo(() => ({ back }), [back]);

  return (
    <MainContentHistoryContext.Provider value={value}>
      {children}
    </MainContentHistoryContext.Provider>
  );
};

const useMainContentHistory = () => useContext(MainContentHistoryContext);

export default useMainContentHistory;
